import React, { useEffect } from 'react'
import { useUnsafeMoteLoopContext } from 'web/src/screens/MoteLanding/MoteLoopProvider'
import { Spinner } from '@mote/ui'
import styled, { css } from '@xstyled/styled-components'
import { useMotePlayer } from '../../shared/contexts/motePlayer.context'
import { MoteCardActiveState } from './states/MoteCardActiveState'
import { MoteCardEndState } from './states/MoteCardEndState'
import { MoteCardIdleState } from './states/MoteCardIdleState'
import { MoteCardRichHeader } from './MoteCardRichHeader'
import { moteCardFluidDimensionsAdjuster } from './fluidSizing'

export const MoteCardRich = ({
  duration,
  audioUrl,
  className = 'mote-card',
  createdByName,
  createdByAvatar,
  createdByUserId,
  createdByGreeting,
  wasCreatedBySameUser,
  displayTitle,
  transcript,
  onPlay,
  onPause,
  onEnd,
  onPlayAgain,
  onDelete,
  shouldEnableReactions,
  isCreator,
  myReactions,
  onShowFullTranscript,
  showLogo = false,
  showSmallAvatarOnIdle = false,
  showReviewOptionsOnIdle = false,
  isStandalone = true,
  visibility,
  shouldShowEndState = true,
  isEditable = false,
  isDeletable = false,
  editor,
  html5 = true
}) => {
  const unsafeMoteLoopContext = useUnsafeMoteLoopContext()
  const [currentTranscript, setCurrentTranscript] = React.useState(transcript)
  const [active, setActive] = React.useState(false)
  const [ended, setEnded] = React.useState(false)
  const [showEditor, setShowEditor] = React.useState(false)

  useEffect(() => {
    setCurrentTranscript(transcript)
  }, [transcript])

  const handleOnInit = React.useCallback(
    (executeCallback = true) => {
      setActive(true)
      executeCallback && onPlay?.()
    },
    [onPlay]
  )

  const handleOnPlay = React.useCallback(() => {
    if (!active) handleOnInit()
    else onPlay?.()
  }, [active, handleOnInit, onPlay])

  const handlePlayAgain = React.useCallback(() => {
    setEnded(false)
    setActive(true)
    onPlayAgain?.()
  }, [onPlayAgain])

  const handleOnDelete = React.useCallback(() => {
    if (!isDeletable) return
    onDelete?.()
  }, [])

  const toggleInitialState = React.useCallback(() => {
    setActive(false)
    setEnded(false)
  }, [])

  const handleOnEnd = React.useCallback(() => {
    if (
      isCreator ||
      (!isCreator && !shouldShowEndState) ||
      unsafeMoteLoopContext?.getIsLoopy()
    ) {
      toggleInitialState()
    } else {
      setActive(false)
      setEnded(true)
    }
    onEnd?.()
  }, [
    isCreator,
    onEnd,
    toggleInitialState,
    unsafeMoteLoopContext?.getIsLoopy,
    shouldShowEndState
  ])

  const options = React.useMemo(() => {
    return {
      init: true,
      html5: html5,
      src: audioUrl,
      isStandalone,
      playbackCallbacks: {
        onPlay: handleOnPlay,
        onPause,
        onReplay: handlePlayAgain,
        onEnd: handleOnEnd,
        onFullTranscript: onShowFullTranscript,
        onDelete: handleOnDelete
      }
    }
  }, [
    handleOnPlay,
    onPause,
    handlePlayAgain,
    handleOnEnd,
    handleOnDelete,
    onShowFullTranscript,
    isStandalone,
    audioUrl,
    html5
  ])

  const {
    theme,
    isReady,
    getPlayerProps,
    playerDimensions,
    active: internalActive,
    ended: internalEnded,
    setActive: setInternalActive,
    setEnded: setInternalEnded
  } = useMotePlayer(options)

  React.useEffect(() => {
    if (internalActive === active) return

    setInternalActive(active)
  }, [active, internalActive])

  React.useEffect(() => {
    if (internalEnded === ended) return

    setInternalEnded(ended)
  }, [ended, internalEnded, setInternalEnded])

  function handleTranscriptLanguageUpdate(newTranscript) {
    setCurrentTranscript(newTranscript)
  }

  return (
    <>
      <Root className={className} moteTheme={theme} {...getPlayerProps()}>
        {!isReady && (
          <LoadingContainer>
            <Spinner
              color='white'
              strokeWidth={2}
              radius={moteCardFluidDimensionsAdjuster(
                105,
                44,
                playerDimensions?.width
              )}
            />
          </LoadingContainer>
        )}
        {active && !ended && currentTranscript && (
          <MoteCardRichHeader
            isCreator={isCreator}
            transcript={currentTranscript}
            createdByName={createdByName}
            createdByAvatar={createdByAvatar}
            onTranscriptLanguageUpdate={handleTranscriptLanguageUpdate}
          />
        )}

        <ResponsiveArea moteTheme={theme} disabled={!isReady}>
          {!active && !ended && (
            <MoteCardIdleState
              isCreator={isCreator}
              isEditable={isEditable}
              isDeletable={isDeletable}
              onInit={handleOnInit}
              createdByName={createdByName}
              createdByAvatar={createdByAvatar}
              createdContext={displayTitle}
              duration={duration}
              showLogo={showLogo}
              showSmallAvatarOnIdle={showSmallAvatarOnIdle}
              showReviewOptions={showReviewOptionsOnIdle}
              onEdit={() => setShowEditor(true)}
              visibility={visibility}
            />
          )}
          {active && !ended && (
            <MoteCardActiveState
              transcript={currentTranscript}
              createdByAvatar={createdByAvatar}
              createdByName={createdByName}
            />
          )}
          {!active && ended && (
            <MoteCardEndState
              createdByGreeting={createdByGreeting}
              createdByUserId={createdByUserId}
              shouldEnableReactions={shouldEnableReactions}
              toggleInitialState={toggleInitialState}
              myReactions={myReactions}
            />
          )}
        </ResponsiveArea>
      </Root>
      {isEditable &&
        Boolean(editor) &&
        editor({
          onClose: () => {
            setShowEditor(false)
          },
          isOpen: showEditor
        })}
    </>
  )
}

const Root = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  min-width: 204px;
  min-height: 204px;
  max-width: 700px;
  max-height: 700px;
  background: ${({ background }) => background};
  border: ${({ border }) => border};
  border-radius: 8px;
  overflow: hidden;
  outline: none;
  box-sizing: border-box;

  &:focus {
    outline: none;
  }

  & * {
    font-family: sans;
  }

  ${({ moteTheme }) => css`
    border: ${moteTheme.border};
  `}
`

const ResponsiveArea = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  position: relative;
  z-index: 1;
  border-radius: 8px;
  transition: filter 150ms;

  &::before {
    content: '';
    width: 0;
    padding-bottom: calc(100% / (1 / 1)); /* here you can place any ratio */
    grid-area: 1 / 1 / 1 / 1;
  }

  & > *:first-of-type {
    grid-area: 1 / 1 / 1 / 1; /* the same as ::before */
  }

  ${({ moteTheme }) => css`
    background: ${moteTheme.background};
  `}

  ${({ disabled }) =>
    disabled &&
    css`
      filter: brightness(0.4);
    `}
`

const LoadingContainer = styled.div`
  position: absolute;
  z-index: 3;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`
